// Minimal cron helper for the Scheduled Jobs UI.
// Supports standard 5-field cron expressions: minute hour day-of-month month day-of-week.
// Each field accepts: *, a, a-b, a-b/n, */n, and comma-separated lists of those.
// Day-of-week: 0 or 7 = Sunday, 1 = Monday … 6 = Saturday.
// All evaluation is in UTC, matching node-cron defaults on the server.

const FIELD_RANGES: Array<[number, number]> = [
    [0, 59], // minute
    [0, 23], // hour
    [1, 31], // day-of-month
    [1, 12], // month
    [0, 6],  // day-of-week (7 normalised to 0)
];

type FieldMatcher = (value: number) => boolean;

const parseField = (raw: string, idx: number): FieldMatcher => {
    const [min, max] = FIELD_RANGES[idx];
    const allowed = new Set<number>();

    raw.split(',').forEach(part => {
        let step = 1;
        let range = part;

        const slashIdx = part.indexOf('/');
        if (slashIdx !== -1) {
            range = part.slice(0, slashIdx);
            step = parseInt(part.slice(slashIdx + 1), 10);
            if (!Number.isFinite(step) || step <= 0) throw new Error(`Invalid step in "${part}"`);
        }

        let lo: number;
        let hi: number;
        if (range === '*' || range === '') {
            lo = min;
            hi = max;
        } else if (range.includes('-')) {
            const [a, b] = range.split('-').map(s => parseInt(s, 10));
            if (!Number.isFinite(a) || !Number.isFinite(b)) throw new Error(`Invalid range "${range}"`);
            lo = a;
            hi = b;
        } else {
            const n = parseInt(range, 10);
            if (!Number.isFinite(n)) throw new Error(`Invalid number "${range}"`);
            // For day-of-week, allow 7 == Sunday (0).
            const normalised = idx === 4 && n === 7 ? 0 : n;
            if (slashIdx === -1) {
                if (normalised < min || normalised > max) {
                    throw new Error(`Value ${n} out of range for field ${idx}`);
                }
                allowed.add(normalised);
                return;
            }
            lo = normalised;
            hi = max;
        }

        if (idx === 4) {
            if (lo === 7) lo = 0;
            if (hi === 7) hi = 0;
        }

        if (lo < min || hi > max || lo > hi) {
            throw new Error(`Range ${lo}-${hi} out of bounds for field ${idx}`);
        }

        for (let v = lo; v <= hi; v += step) allowed.add(v);
    });

    return (v: number) => allowed.has(v);
};

export interface ParsedCron {
    minute: FieldMatcher;
    hour: FieldMatcher;
    dom: FieldMatcher;
    month: FieldMatcher;
    dow: FieldMatcher;
    raw: string;
    fields: string[];
    domRestricted: boolean;
    dowRestricted: boolean;
}

export const parseCron = (expr: string): ParsedCron => {
    const trimmed = expr.trim().replace(/\s+/g, ' ');
    if (!trimmed) throw new Error('Empty expression');
    const fields = trimmed.split(' ');
    if (fields.length !== 5) {
        throw new Error('Expected 5 fields (minute hour day-of-month month day-of-week)');
    }
    return {
        minute: parseField(fields[0], 0),
        hour: parseField(fields[1], 1),
        dom: parseField(fields[2], 2),
        month: parseField(fields[3], 3),
        dow: parseField(fields[4], 4),
        raw: trimmed,
        fields,
        domRestricted: fields[2] !== '*',
        dowRestricted: fields[4] !== '*',
    };
};

// Compute the next N run times after `from` (exclusive). All evaluation in UTC.
export const nextRuns = (expr: string, count = 3, from: Date = new Date()): Date[] => {
    const parsed = parseCron(expr);
    const results: Date[] = [];
    // Step minute-by-minute; skip whole days when day fields don't match.
    const cursor = new Date(Date.UTC(
        from.getUTCFullYear(),
        from.getUTCMonth(),
        from.getUTCDate(),
        from.getUTCHours(),
        from.getUTCMinutes() + 1, // strictly after
        0,
        0,
    ));
    const limit = new Date(cursor.getTime() + 366 * 24 * 60 * 60 * 1000);

    while (cursor <= limit && results.length < count) {
        const month = cursor.getUTCMonth() + 1;
        const dom = cursor.getUTCDate();
        const dow = cursor.getUTCDay();

        const monthOk = parsed.month(month);
        // Vixie cron semantics: when both DOM and DOW are restricted, match if EITHER matches.
        let dayOk: boolean;
        if (parsed.domRestricted && parsed.dowRestricted) {
            dayOk = parsed.dom(dom) || parsed.dow(dow);
        } else {
            dayOk = parsed.dom(dom) && parsed.dow(dow);
        }

        if (!monthOk || !dayOk) {
            cursor.setUTCHours(0, 0, 0, 0);
            cursor.setUTCDate(cursor.getUTCDate() + 1);
            continue;
        }

        const hour = cursor.getUTCHours();
        if (!parsed.hour(hour)) {
            cursor.setUTCMinutes(0, 0, 0);
            cursor.setUTCHours(hour + 1);
            continue;
        }

        if (parsed.minute(cursor.getUTCMinutes())) {
            results.push(new Date(cursor));
        }
        cursor.setUTCMinutes(cursor.getUTCMinutes() + 1);
    }

    return results;
};

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const DAY_NAMES_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const describeField = (raw: string, idx: number): string => {
    if (raw === '*') return idx === 0 ? 'every minute' : '';
    // Step
    const slash = raw.indexOf('/');
    if (slash !== -1 && raw.startsWith('*/')) {
        const step = raw.slice(2);
        const unit = ['minutes', 'hours', 'days', 'months', 'days of week'][idx];
        return `every ${step} ${unit}`;
    }
    // List or single
    return raw;
};

const describeDow = (raw: string): string => {
    if (raw === '*') return 'every day';
    if (raw === '1-5') return 'Mon–Fri';
    if (raw === '0,6' || raw === '6,0' || raw === '0,7') return 'weekends';
    if (raw.includes('-') && !raw.includes(',') && !raw.includes('/')) {
        const [a, b] = raw.split('-').map(n => parseInt(n, 10) % 7);
        return `${DAY_NAMES_SHORT[a]}–${DAY_NAMES_SHORT[b]}`;
    }
    if (!raw.includes('-') && !raw.includes('/')) {
        const days = raw.split(',').map(n => DAY_NAMES[parseInt(n, 10) % 7]).filter(Boolean);
        if (days.length) return days.join(', ');
    }
    return `day-of-week ${raw}`;
};

const pad2 = (n: number) => String(n).padStart(2, '0');

// Best-effort human readable summary. Falls back to a generic description.
export const describeCron = (expr: string): string => {
    let parsed: ParsedCron;
    try {
        parsed = parseCron(expr);
    } catch (err) {
        return `Invalid: ${(err as Error).message}`;
    }
    const [m, h, dom, mon, dow] = parsed.fields;

    // Time-of-day phrase
    let timePhrase: string;
    if (m === '*' && h === '*') {
        timePhrase = 'every minute';
    } else if (m === '*' && h !== '*') {
        timePhrase = `every minute of hour ${h}`;
    } else if (m.startsWith('*/') && h === '*') {
        timePhrase = `every ${m.slice(2)} minutes`;
    } else if (h.startsWith('*/') && /^\d+$/.test(m)) {
        timePhrase = `at minute ${m} of every ${h.slice(2)} hours`;
    } else if (/^\d+$/.test(m) && /^\d+$/.test(h)) {
        timePhrase = `at ${pad2(parseInt(h, 10))}:${pad2(parseInt(m, 10))}`;
    } else {
        timePhrase = `min=${m} hour=${h}`;
    }

    // Day phrase
    const parts: string[] = [timePhrase];
    if (dow !== '*') {
        parts.push(`on ${describeDow(dow)}`);
    }
    if (dom !== '*') {
        parts.push(`on day ${dom} of the month`);
    }
    if (mon !== '*') {
        parts.push(`in month ${describeField(mon, 3)}`);
    }

    return parts.join(' ') + ' (UTC)';
};

export interface CronPreset {
    label: string;
    value: string;
    description: string;
}

export const CRON_PRESETS: CronPreset[] = [
    { label: 'Every 15 minutes', value: '*/15 * * * *', description: 'Runs four times an hour' },
    { label: 'Every hour', value: '0 * * * *', description: 'Top of every hour' },
    { label: 'Every 6 hours', value: '0 */6 * * *', description: '00:00, 06:00, 12:00, 18:00 UTC' },
    { label: 'Daily at 06:00', value: '0 6 * * *', description: 'Once a day, early morning UTC' },
    { label: 'Daily at 09:00', value: '0 9 * * *', description: 'Once a day, 09:00 UTC' },
    { label: 'Weekdays at 09:00', value: '0 9 * * 1-5', description: 'Mon–Fri, 09:00 UTC' },
    { label: 'Weekly (Mon 09:00)', value: '0 9 * * 1', description: 'Every Monday at 09:00 UTC' },
    { label: 'Monthly (1st at 06:00)', value: '0 6 1 * *', description: 'First of the month, 06:00 UTC' },
];

export const isValidCron = (expr: string): boolean => {
    try {
        parseCron(expr);
        return true;
    } catch {
        return false;
    }
};
